import { BUBBLE_CSS_PRESETS, BUBBLE_THEMES } from '@proj-airi/stage-ui/constants'
import { useSettings } from '@proj-airi/stage-ui/stores/settings'
import { useDark } from '@vueuse/core'
import { watch } from 'vue'

let customStyleElement: HTMLStyleElement | null = null

/**
 * 将颜色变量应用到 document
 */
function setCssVariables(colors: { userBg: string, userText: string, aiBg: string, aiText: string }) {
  document.documentElement.style.setProperty('--chat-bubble-user-bg', colors.userBg)
  document.documentElement.style.setProperty('--chat-bubble-user-text', colors.userText)
  document.documentElement.style.setProperty('--chat-bubble-ai-bg', colors.aiBg)
  document.documentElement.style.setProperty('--chat-bubble-ai-text', colors.aiText)
}

type BubbleThemeKey = keyof typeof BUBBLE_THEMES | keyof typeof BUBBLE_CSS_PRESETS | 'custom'

/**
 * Apply chat bubble theme to the document
 * @param theme 主题名称或 'custom'
 * @param isDark 当前是否为暗色模式
 * @param customCss 自定义 CSS（仅 theme 为 'custom' 时生效）
 */
function applyBubbleTheme(theme: BubbleThemeKey, isDark: boolean, customCss?: string) {
  if (customStyleElement) {
    customStyleElement.remove()
    customStyleElement = null
  }

  if (theme === 'custom' && customCss) {
    customStyleElement = document.createElement('style')
    customStyleElement.id = 'custom-bubble-style'
    customStyleElement.textContent = customCss
    document.head.appendChild(customStyleElement)
  }
  else if (theme in BUBBLE_CSS_PRESETS) {
    customStyleElement = document.createElement('style')
    customStyleElement.id = 'custom-bubble-style'
    customStyleElement.textContent = BUBBLE_CSS_PRESETS[theme as keyof typeof BUBBLE_CSS_PRESETS]
    document.head.appendChild(customStyleElement)
  }
  else if (theme in BUBBLE_THEMES) {
    const themeColors = BUBBLE_THEMES[theme as keyof typeof BUBBLE_THEMES]
    setCssVariables(isDark ? themeColors.dark : themeColors.light)
  }
  else {
    const themeColors = BUBBLE_THEMES.default
    setCssVariables(isDark ? themeColors.dark : themeColors.light)
  }
}

/**
 * Composable to use chat bubble theming
 */
export function useChatBubbleTheme() {
  const settings = useSettings()
  const isDark = useDark()

  // Apply theme on mount and when settings or dark mode change
  watch(
    () => [settings.chatBubbleTheme, settings.chatBubbleCustomCss, isDark.value] as const,
    ([theme, customCss, dark]) => {
      applyBubbleTheme(theme, dark, customCss)
    },
    { immediate: true },
  )

  return {
    applyBubbleTheme,
  }
}
